import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useAppStore } from '../../store';
import { createTask } from '../../hooks/useTasks';
import { queryClient } from '../../api/queryClient';
import type { TaskStatus, ItemType } from '../../types';

const presetColors = ['#3b82f6', '#8b5cf6', '#ef4444', '#f59e0b', '#10b981', '#ec4899', '#06b6d4', '#f97316'];

interface QuickAddProps {
  defaultStatus?: TaskStatus;
  trackDate?: string;
  placeholder?: string;
}

export function QuickAdd({
  defaultStatus = 'backlog',
  trackDate,
  placeholder = 'Add...',
}: QuickAddProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [itemType, setItemType] = useState<ItemType>('task');
  const [title, setTitle] = useState('');
  const [color, setColor] = useState(presetColors[0]);
  const spaceId = useAppStore((s) => s.currentSpaceId);

  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('tutorial:open-quick-add', handler);
    return () => window.removeEventListener('tutorial:open-quick-add', handler);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    await createTask(title.trim(), spaceId, 'p2', defaultStatus, {
      trackDate,
      itemType,
      color: itemType === 'project' ? color : undefined,
    });
    window.dispatchEvent(new CustomEvent('tutorial:task-created'));
    if (defaultStatus === 'track') {
      queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] });
    } else {
      queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'backlog'] });
    }
    reset();
  };

  const reset = () => {
    setTitle('');
    setItemType('task');
    setIsOpen(false);
  };

  return (
    <div data-tutorial="quick-add">
    <AnimatePresence mode="wait" initial={false}>
      {!isOpen ? (
        <motion.button
          key="trigger"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-caption text-text-muted hover:text-text-secondary interactive-ghost font-medium"
        >
          <Plus size={15} />
          {placeholder}
        </motion.button>
      ) : (
        <motion.form
          key="form"
          initial={{ opacity: 0, scale: 0.97, y: -4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97, y: -4 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          onSubmit={handleSubmit}
          className="surface-raised p-4 flex flex-col gap-3 w-[380px]"
        >
          <div className="flex items-center gap-2.5">
            <Plus size={16} className="text-accent shrink-0" />
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && reset()}
              placeholder={itemType === 'project' ? 'New project...' : 'New task...'}
              aria-label={itemType === 'project' ? 'New project name' : 'New task name'}
              maxLength={200}
              className="flex-1 bg-transparent text-body text-text-primary placeholder:text-text-muted focus:outline-none"
            />
          </div>

          {/* Color picker for projects */}
          {itemType === 'project' && (
            <div className="flex items-center gap-2 pl-7">
              {presetColors.slice(0, 6).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  aria-label={`Select color ${c}`}
                  aria-pressed={color === c}
                  className={`w-7 h-7 rounded-full transition-transform ${color === c ? 'ring-2 ring-accent ring-offset-2 ring-offset-bg-raised scale-110' : 'hover:scale-110'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          )}

          <div className="flex items-center justify-between pt-3 border-t border-border-subtle">
            <div className="flex items-center bg-bg-app rounded-lg p-0.5 border border-border-subtle">
              <button
                type="button"
                onClick={() => setItemType('task')}
                className={`px-3 py-1.5 text-micro font-medium rounded-md transition-colors ${
                  itemType === 'task' ? 'bg-accent/10 text-accent' : 'text-text-muted'
                }`}
              >
                Task
              </button>
              <button
                type="button"
                onClick={() => setItemType('project')}
                className={`px-3 py-1.5 text-micro font-medium rounded-md transition-colors ${
                  itemType === 'project' ? 'bg-accent/10 text-accent' : 'text-text-muted'
                }`}
              >
                Project
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={reset} className="text-micro text-text-muted hover:text-text-secondary px-3 py-1.5">
                Esc
              </button>
              <button
                type="submit"
                className="px-4 py-1.5 text-micro font-semibold bg-accent text-bg-base rounded-lg hover:bg-accent-hover hover:shadow-lg hover:shadow-accent/20 transition-all"
              >
                Add
              </button>
            </div>
          </div>
        </motion.form>
      )}
    </AnimatePresence>
    </div>
  );
}
